import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseInfiniteQueryOptions, useInfiniteQuery } from 'react-query'
import { BrandPlanScheduleListItem } from 'types'
import { Response } from 'request-dot-js/index'
import qs from 'qs'
import { getNextPageParam } from 'utils/getNextPageParam'
import { reportError } from 'utils/error'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { mapKeys, snakeCase } from 'lodash'
import { SchedulesData, SchedulesPage } from './types'

interface Params {
  page: number
  items?: number
  startDate?: string
  endDate?: string
  brandPlanId?: number
  orderBy?: 'start_date'
  orderDirection?: 'asc' | 'desc'
}

export interface Props extends Omit<Params, 'page'> {}

export const fetchSchedules = async ({ workspaceId, params }: { workspaceId: number; params: Params }) => {
  const stringifiedParams = qs.stringify(mapKeys(params, (_, key) => snakeCase(key)))
  const res = await sendToApi<{ schedules: BrandPlanScheduleListItem[] }>(
    `/w/${workspaceId}/brand_plan_schedules?${stringifiedParams}`,
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res
}

export interface Options<TData> extends UseInfiniteQueryOptions<Response<SchedulesData>, unknown, TData> {}

export const useSchedules = <TData = Response<SchedulesData>,>(props?: Props, options?: Options<TData>) => {
  const { items = 5, ...rest } = props || {}
  const workspaceId = useCurrentWorkspaceId()

  return useInfiniteQuery<SchedulesPage, unknown, TData>(
    ['workspaces', workspaceId, 'schedules', 'list', { items, ...rest }],
    async ({ pageParam = 1 }) =>
      fetchSchedules({
        workspaceId,
        params: { ...rest, page: pageParam, items },
      }),
    {
      refetchOnWindowFocus: false,
      getNextPageParam,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
      ...options,
    },
  )
}
